import { SECURITY_GROUP_CATALOG, type SecurityGroupKey } from 'edinshall-people-shared';

export type PermissionsManagementMode = 'local' | 'hybrid' | 'portal';

export type PermissionsGroupKey = SecurityGroupKey;
export type AssignmentPrincipalKind = 'user' | 'security_group' | 'm365_group';

export interface BaselineAdmin {
  email: string;
  displayName?: string;
}

export interface AssignmentRule {
  ruleId: string;
  groupKey: PermissionsGroupKey | string;
  principalKind?: AssignmentPrincipalKind;
  principalIdentifier?: string;
  principalEmail?: string;
  principalDisplayName?: string;
  targetMode: 'all_instances' | 'instances';
  targetInstanceIds: string[];
  isActive: boolean;
}

export interface PermissionsPolicy {
  tenantId: string;
  managementMode: PermissionsManagementMode;
  enforceSystemAdmins: boolean;
  baselineSystemAdmins: BaselineAdmin[];
  assignmentRules: AssignmentRule[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface GroupCoverage {
  key: string;
  name: string;
  critical: boolean;
  presentOnInstances: number;
  totalInspectableInstances: number;
}

export interface PostureSummary {
  totalInstances: number;
  inspectableInstances: number;
  staleInstances: number;
  healthyInstances: number;
  missingCriticalGroupInstances: number;
  groupCoverage: GroupCoverage[];
}

export interface PostureGroup {
  key: string;
  name: string;
  critical: boolean;
  exists: boolean;
  memberCount: number;
}

export interface PostureInstance {
  instanceId: string;
  siteUrl: string;
  webPartVersion: string;
  status: 'active' | 'recent' | 'stale' | 'deregistered';
  daysSinceLastSeen: number;
  inspectable: boolean;
  groups: PostureGroup[];
  expectedMembersByGroupKey: Record<string, string[]>;
  missingExpectedMembersByGroupKey: Record<string, string[]>;
  isCompliantWithPolicy: boolean;
  error?: string;
}

export interface PostureResponse {
  inspectorConfigured: boolean;
  generatedAt: string;
  policy: PermissionsPolicy;
  summary: PostureSummary;
  instances: PostureInstance[];
}

export interface EnterpriseBaselinePolicy {
  subscriptionId: string;
  baselineAdmins: BaselineAdmin[];
  assignmentRules: AssignmentRule[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface BaselineAggregateStateTenant {
  tenantId: string;
  generatedAt?: string;
  summary?: PostureSummary;
  policyUpdatedAt?: string | null;
  error?: string;
}

export interface BaselineAggregateState {
  subscriptionId: string;
  coveredTenantIds: string[];
  tenants: BaselineAggregateStateTenant[];
}

export interface BaselineAggregateDriftTenant {
  tenantId: string;
  openCount?: number;
  lastScanAt?: string | null;
  lastScanBy?: string | null;
  error?: string;
}

export interface BaselineAggregateDrift {
  subscriptionId: string;
  coveredTenantIds: string[];
  tenants: BaselineAggregateDriftTenant[];
}

export type PermissionsTab = 'state' | 'policy' | 'apply' | 'drift';
export type OverallState = 'healthy' | 'warning' | 'at-risk';
export type CoverageCellState = 'filled' | 'empty' | 'missing' | 'critical-missing' | 'not-checked';

/**
 * The standard product-managed group catalogue. Customer admins do NOT extend
 * this list — every entry maps to real code in SPFx + Portal. Closed decision
 * from the design doc review.
 */
export const KNOWN_GROUP_CATALOG: Array<{ key: PermissionsGroupKey; name: string; description: string }> =
  SECURITY_GROUP_CATALOG.filter((group) => group.portalVisible).map((group) => ({
    key: group.key,
    name: group.title,
    description: group.description
  }));
